import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { getTrackingNumbers } from "@redotech/redo-fulfillment-group-common/fulfillment-group-tracking";
import { useTranslation } from "@redotech/redo-i18n";
import TruckIcon from "@redotech/redo-icons/truck-01.svg";
import { ClientUploader } from "@redotech/redo-merchant-app-common/client/upload";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { isFedexDamageInfoComplete } from "@redotech/redo-package-protection-common/shipping-claims/carrier-claim-info/fedex";
import { isUpsDamageInfoComplete } from "@redotech/redo-package-protection-common/shipping-claims/carrier-claim-info/ups";
import {
  carrierToSubmissionMethod,
  ClaimSubmissionMethod,
} from "@redotech/redo-package-protection-common/shipping-claims/claim-submission-method";
import {
  CarrierSpecificClaimInfo,
  ShippingClaimPhoto,
  ShippingClaimReason,
} from "@redotech/redo-package-protection-common/shipping-claims/shipping-claim";
import { toast } from "@redotech/redo-web/alert";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import { UploaderContext } from "@redotech/redo-web/image-upload";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { getEffectiveShipmentSummary } from "../../utils/effective-shipment-summary";
import { convertHeicToJpeg } from "../heic-converter";
import {
  buildFedexClaimInfo,
  FedexClaimInfoSection,
  PartialFedexDamageInfo,
} from "./carrier-claim-info/fedex-claim-info-section";
import {
  buildUpsClaimInfo,
  PartialUpsDamageInfo,
  UpsClaimInfoSection,
} from "./carrier-claim-info/ups-claim-info-section";
import { PhotoUploadSection } from "./photo-upload-section";
import { getShippingClaimReasonLabel } from "./shipping-claim-labels";

interface OmsShippingClaimInput {
  type: "oms";
  fulfillmentGroup: FulfillmentGroupData;
}

interface ReturnShippingClaimInput {
  type: "returns";
  returnId: string;
  shipmentId: string;
}

type ShippingClaimInput = OmsShippingClaimInput | ReturnShippingClaimInput;

interface SubmitShippingClaimModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
  shippingClaimInput: ShippingClaimInput;
}

export const SubmitShippingClaimModalWrapper = memo(
  function SubmitShippingClaimModalWrapper({
    isOpen,
    onClose,
    onSubmitted,
    shippingClaimInput,
  }: SubmitShippingClaimModalProps) {
    return (
      <ClientUploader>
        <SubmitShippingClaimModal
          isOpen={isOpen}
          onClose={onClose}
          onSubmitted={onSubmitted}
          shippingClaimInput={shippingClaimInput}
        />
      </ClientUploader>
    );
  },
);

export const SubmitShippingClaimModal = memo(function SubmitShippingClaimModal({
  isOpen,
  onClose,
  onSubmitted,
  shippingClaimInput,
}: SubmitShippingClaimModalProps) {
  const { t } = useTranslation();
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const uploader = useRequiredContext(UploaderContext);

  const [reason, setReason] = useState<ShippingClaimReason>(
    ShippingClaimReason.LATE,
  );
  const [filesWithDescriptions, setFilesWithDescriptions] = useState<
    { file: File; description: string }[]
  >([]);
  const [notes, setNotes] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [selectedTrackingNumber, setSelectedTrackingNumber] = useState<
    string | null
  >(null);
  const [upsDamage, setUpsDamage] = useState<PartialUpsDamageInfo>({});
  const [fedexDamage, setFedexDamage] = useState<PartialFedexDamageInfo>({});

  const resetForm = useHandler(() => {
    setReason(ShippingClaimReason.LATE);
    setFilesWithDescriptions([]);
    setNotes("");
    setSelectedTrackingNumber(null);
    setUpsDamage({});
    setFedexDamage({});
  });

  const handleFilesAdded = useHandler((newFiles: File[]) => {
    const newFilesWithDescriptions = newFiles.map((file) => ({
      file,
      description: "",
    }));
    setFilesWithDescriptions([
      ...filesWithDescriptions,
      ...newFilesWithDescriptions,
    ]);
  });

  const handleFileDeleted = useHandler((index: number) => {
    const newFiles = [...filesWithDescriptions];
    newFiles.splice(index, 1);
    setFilesWithDescriptions(newFiles);
  });

  const handleDescriptionChange = useHandler(
    (index: number, description: string) => {
      setFilesWithDescriptions((prev) =>
        prev.map((item, i) => (i === index ? { ...item, description } : item)),
      );
    },
  );

  const uploadFilesToS3 = useHandler(async () => {
    if (filesWithDescriptions.length === 0) {
      return [];
    }

    setIsUploading(true);

    try {
      const uploadPromises = filesWithDescriptions.map(
        async ({ file, description }) => {
          let fileToUpload = file;

          try {
            fileToUpload = await convertHeicToJpeg(file);
          } catch (conversionError) {
            console.error("Error converting HEIC file:", conversionError);
            toast(
              t(
                "Failed to convert HEIC image. Please convert to JPEG before uploading.",
              ),
              { variant: "error" },
            );
            throw conversionError;
          }

          const result = await uploader({
            file: fileToUpload,
            autoOptimizeImage: true,
            signal: AbortSignal.timeout(20_000),
          });

          return {
            url: result.url.toString(),
            contentType: fileToUpload.type,
            fileName: fileToUpload.name,
            uploadedAt: new Date(),
            description: description.trim() || undefined,
          };
        },
      );

      return await Promise.all(uploadPromises);
    } catch (error) {
      console.error("Error uploading files:", error);
      toast(t("Error uploading photos. Please try again."), {
        variant: "error",
      });
      throw error;
    } finally {
      setIsUploading(false);
    }
  });

  const availableTrackingNumbers = useMemo(() => {
    if (shippingClaimInput.type !== "oms") {
      return [];
    }
    const allTrackingNumbers = getTrackingNumbers(
      shippingClaimInput.fulfillmentGroup,
    );
    const claimIdsByTrackingNumber =
      shippingClaimInput.fulfillmentGroup.shippingClaimIdsByTrackingNumber ||
      {};
    return allTrackingNumbers.filter(
      (trackingNumber) =>
        !claimIdsByTrackingNumber[trackingNumber] ||
        claimIdsByTrackingNumber[trackingNumber].length === 0,
    );
  }, [shippingClaimInput]);

  const effectiveTrackingNumber =
    shippingClaimInput.type === "oms"
      ? selectedTrackingNumber || availableTrackingNumbers[0] || null
      : null;

  const shouldShowTrackingNumberDropdown =
    shippingClaimInput.type === "oms" &&
    getTrackingNumbers(shippingClaimInput.fulfillmentGroup).length > 1;

  const claimMethod = useMemo(() => {
    if (shippingClaimInput.type !== "oms" || !effectiveTrackingNumber) {
      return null;
    }
    const carrierShipment = getEffectiveShipmentSummary(
      shippingClaimInput.fulfillmentGroup,
    )?.carrierShipments.find((cs) =>
      cs.packages.some((p) => p.trackingCode === effectiveTrackingNumber),
    );
    return carrierToSubmissionMethod(carrierShipment?.carrier ?? undefined);
  }, [shippingClaimInput, effectiveTrackingNumber]);

  const showUpsSection =
    claimMethod === ClaimSubmissionMethod.UPS &&
    reason === ShippingClaimReason.DAMAGED;
  const showFedexSection =
    claimMethod === ClaimSubmissionMethod.FEDEX &&
    reason === ShippingClaimReason.DAMAGED;

  const carrierSpecificInfo: CarrierSpecificClaimInfo | null = useMemo(() => {
    if (showUpsSection) {
      const ups = buildUpsClaimInfo(upsDamage);
      return ups ? { ups } : null;
    }
    if (showFedexSection) {
      const fedex = buildFedexClaimInfo(fedexDamage);
      return fedex ? { fedex } : null;
    }
    return null;
  }, [showUpsSection, showFedexSection, upsDamage, fedexDamage]);

  const [submitClaimLoad, triggerSubmitClaim] = useTriggerLoad(async () => {
    try {
      let photos: ShippingClaimPhoto[] = [];

      if (filesWithDescriptions.length > 0) {
        photos = await uploadFilesToS3();
      }

      let response;

      switch (shippingClaimInput.type) {
        case "oms":
          response = await rpcClient.submitShippingClaim({
            fulfillmentGroupId: shippingClaimInput.fulfillmentGroup._id,
            trackingNumber: effectiveTrackingNumber || undefined,
            reason,
            photos,
            notes: notes.trim() || undefined,
            carrierSpecificInfo,
          });
          break;
        case "returns":
          response = await merchantRpcClient.submitReturnShippingClaim({
            returnId: shippingClaimInput.returnId,
            shipmentId: shippingClaimInput.shipmentId,
            reason,
            photos,
            notes: notes.trim() || undefined,
            carrierSpecificInfo,
          });
          break;
        default:
          assertNever(shippingClaimInput);
      }

      if (response.success) {
        toast(t("Carrier claim request submitted successfully"), {
          variant: "success",
        });
        onClose();
        resetForm();
        onSubmitted?.();
      } else {
        toast(
          t("Failed to submit carrier claim: {{message}}", {
            message: response.message,
          }),
          { variant: "error" },
        );
      }
    } catch (error) {
      toast(t("Failed to submit carrier claim"), { variant: "error" });
      console.error("Error submitting claim:", error);
    }
  });

  const canSubmit = useMemo(() => {
    // Photos are required for damaged claims
    if (
      reason === ShippingClaimReason.DAMAGED &&
      filesWithDescriptions.length === 0
    ) {
      return false;
    }
    // Tracking number is required for multi-parcel OMS claims
    if (shouldShowTrackingNumberDropdown && !effectiveTrackingNumber) {
      return false;
    }
    if (showUpsSection && !isUpsDamageInfoComplete(upsDamage)) {
      return false;
    }
    if (showFedexSection && !isFedexDamageInfoComplete(fedexDamage)) {
      return false;
    }
    return true;
  }, [
    reason,
    filesWithDescriptions,
    shouldShowTrackingNumberDropdown,
    effectiveTrackingNumber,
    showUpsSection,
    showFedexSection,
    upsDamage,
    fedexDamage,
  ]);

  const handleSubmit = useHandler(async () => {
    if (canSubmit) {
      triggerSubmitClaim();
    }
  });

  const isPending = submitClaimLoad.pending || isUploading;

  const reasonOptions: RedoListItem<ShippingClaimReason>[] = Object.values(
    ShippingClaimReason,
  ).map((reason) => ({
    type: "text",
    text: getShippingClaimReasonLabel(reason, t),
    value: reason,
    id: reason,
  }));

  const trackingNumberOptions: RedoListItem<string>[] = useMemo(() => {
    if (shippingClaimInput.type !== "oms") {
      return [];
    }
    const allTrackingNumbers = getTrackingNumbers(
      shippingClaimInput.fulfillmentGroup,
    );

    return availableTrackingNumbers.map((trackingNumber) => {
      const originalIndex = allTrackingNumbers.indexOf(trackingNumber);
      return {
        type: "text",
        text: t("Package {{number}}: {{trackingNumber}}", {
          number: originalIndex + 1,
          trackingNumber,
        }),
        value: trackingNumber,
        id: trackingNumber,
      };
    });
  }, [availableTrackingNumbers, shippingClaimInput, t]);

  const shipDate = useMemo<Date | null>(() => {
    if (shippingClaimInput.type !== "oms" || !effectiveTrackingNumber) {
      return null;
    }
    const carrierShipment = getEffectiveShipmentSummary(
      shippingClaimInput.fulfillmentGroup,
    )?.carrierShipments.find((cs) =>
      cs.packages.some((p) => p.trackingCode === effectiveTrackingNumber),
    );
    const pkg = carrierShipment?.packages.find(
      (p) => p.trackingCode === effectiveTrackingNumber,
    );
    if (!pkg) {
      return null;
    }
    if (pkg.firstScanAt) {
      return pkg.firstScanAt;
    }
    return carrierShipment?.purchasedAt ?? null;
  }, [shippingClaimInput, effectiveTrackingNumber]);

  const daysSinceShip = useMemo<number | null>(() => {
    if (!shipDate) {
      return null;
    }
    return Math.floor(
      (Date.now() - shipDate.getTime()) / (1000 * 60 * 60 * 24),
    );
  }, [shipDate]);

  const pastClaimWindow = daysSinceShip !== null && daysSinceShip > 60;

  const subtitle =
    shippingClaimInput.type === "returns"
      ? t(
          "Important: This claim is for the return shipment, not the original outbound shipment",
        )
      : undefined;

  const getRequiredDocumentation = (): string[] => {
    switch (reason) {
      case ShippingClaimReason.LOST:
        return [t("The complete invoice which shows the buyer details")];
      case ShippingClaimReason.STOLEN:
        return [
          t("The complete invoice which shows the buyer details"),
          t("A screenshot from the customer saying the order was stolen"),
        ];
      case ShippingClaimReason.DAMAGED:
        return [
          t("The complete invoice which shows the buyer details"),
          t("A photo of the damaged item"),
          t("A photo of the inner packaging"),
          t("A photo of the outer packaging"),
        ];
      case ShippingClaimReason.LATE:
        return [];
      default:
        return [];
    }
  };
  const neededDocumentation = getRequiredDocumentation();

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="md"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: t("Submit claim request"),
        onClick: handleSubmit,
        pending: isPending,
        size: "sm",
        disabled: !canSubmit,
      }}
      secondaryButton={{ text: t("Cancel"), onClick: onClose, size: "sm" }}
      subtitle={subtitle}
      title={t("Submit a carrier claim request")}
      TitleIcon={() => <Icon arbiterIconSvg={TruckIcon} color="warning" />}
    >
      <Flex dir="column" gap="lg">
        {pastClaimWindow && (
          <RedoAlert
            color="warning"
            description={t(
              "This package shipped {{count}} days ago. Most carriers reject claims filed more than 60 days after shipment.",
              { count: daysSinceShip },
            )}
            fullWidth
            title={t("Outside the typical claim window")}
          />
        )}
        {shouldShowTrackingNumberDropdown && (
          <RedoSingleSelectDropdownInput
            label={t("Select package")}
            options={trackingNumberOptions}
            optionSelected={(item) => {
              setSelectedTrackingNumber(item.item.value);
            }}
            selectedItem={trackingNumberOptions.find(
              (item) => item.value === effectiveTrackingNumber,
            )}
          />
        )}

        <RedoSingleSelectDropdownInput
          label={t("Claim reason")}
          options={reasonOptions}
          optionSelected={(item) => {
            setReason(item.item.value);
          }}
          selectedItem={reasonOptions.find((item) => item.value === reason)}
        />
        {neededDocumentation.length > 0 && (
          <Flex dir="column" gap="sm">
            <Text fontSize="sm" fontWeight="semibold">
              {t("Required documentation")}
            </Text>
            <Flex dir="column" gap="xs">
              {neededDocumentation.map((documentation, index) => (
                <Text fontSize="sm" key={index}>
                  • {documentation}
                </Text>
              ))}
            </Flex>
          </Flex>
        )}
        <PhotoUploadSection
          filesWithDescriptions={filesWithDescriptions}
          maxFiles={5}
          onDescriptionChange={handleDescriptionChange}
          onFileDeleted={handleFileDeleted}
          onFilesAdded={handleFilesAdded}
          required={reason === ShippingClaimReason.DAMAGED}
        />

        {showUpsSection && (
          <UpsClaimInfoSection setValue={setUpsDamage} value={upsDamage} />
        )}
        {showFedexSection && (
          <FedexClaimInfoSection
            setValue={setFedexDamage}
            value={fedexDamage}
          />
        )}

        <Flex dir="column" gap="md">
          <RedoTextareaInputField
            label={t("Notes (optional)")}
            placeholder={t("Add any additional details about the claim...")}
            setValue={(value) => setNotes(value)}
            value={notes}
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});
